import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../icons';
import { IconButton } from './Button';
import { useT } from '../../i18n';

const TONE_ICON = {
  info: Icon.Info,
  success: Icon.CircleCheck,
  warning: Icon.TriangleAlert,
  danger: Icon.CircleAlert,
  brand: Icon.Sparkles,
  neutral: Icon.Info,
};

const TONE_ROLE = { danger: 'alert', warning: 'alert' };

/**
 * Toast — a single transient notification card.
 *
 * Usually you won't render this directly; use `useToast()` from ToastProvider.
 * It is exported for static showcases and custom viewports.
 *
 * Props:
 *   tone: 'info'|'success'|'warning'|'danger'|'brand'|'neutral'
 *   title: ReactNode                 — bold lead line
 *   description: ReactNode           — optional secondary text
 *   icon: ReactNode | null           — override / hide the tone icon
 *   action: { label, onClick } | ReactNode
 *   duration: number (ms)            — 0 / null keeps it until dismissed
 *   onDismiss: (id) => void
 *   id: string
 */
function Toast({
  id,
  tone = 'neutral',
  title,
  description,
  icon,
  action,
  duration = 5000,
  onDismiss,
}) {
  const t = useT();
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);

  const close = useCallback(() => setLeaving(true), []);

  useEffect(() => {
    if (!duration || paused || leaving) return undefined;
    const t = setTimeout(close, duration);
    return () => clearTimeout(t);
  }, [duration, paused, leaving, close]);

  const handleAnimEnd = () => {
    if (leaving && onDismiss) onDismiss(id);
  };

  const ToneIcon = TONE_ICON[tone] || Icon.Info;
  const resolvedIcon = icon === undefined ? <ToneIcon size={18} /> : icon;

  let actionEl = null;
  if (action) {
    actionEl = React.isValidElement(action) ? action : (
      <button
        type="button"
        className="ps-toast__action"
        onClick={() => { action.onClick?.(); close(); }}
      >
        {action.label}
      </button>
    );
  }

  return (
    <div
      className={`ps-toast ps-toast--${tone}${leaving ? ' is-leaving' : ''}`}
      role={TONE_ROLE[tone] || 'status'}
      aria-live={TONE_ROLE[tone] ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onAnimationEnd={handleAnimEnd}
    >
      {resolvedIcon && <span className="ps-toast__icon">{resolvedIcon}</span>}
      <div className="ps-toast__content">
        {title && <p className="ps-toast__title">{title}</p>}
        {description && <div className="ps-toast__desc">{description}</div>}
      </div>
      {actionEl && <div className="ps-toast__actions">{actionEl}</div>}
      <IconButton
        aria-label={t('Dismiss notification')}
        variant="ghost"
        size="icon-sm"
        className="ps-toast__close"
        onClick={close}
      >
        <Icon.X />
      </IconButton>
      {duration > 0 && (
        <span
          className="ps-toast__progress"
          style={{ animationDuration: `${duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}
        />
      )}
    </div>
  );
}

export default Toast;
